import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (movies.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % movies.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [movies.length]);

  if (movies.length === 0) return null;

  const goTo = (next: number) => {
    setIndex((next + movies.length) % movies.length);
  };

  return (
    <section className="relative w-full h-[440px] md:h-[560px] overflow-hidden rounded-none md:rounded-xl bg-foreground-950">
      {movies.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={movie.backdrop}
            alt={`${movie.title} 剧照`}
            title={`${movie.title} ${movie.year} 影视资源`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20" />
        </div>
      ))}

      <div className="relative z-10 h-full w-full flex items-center">
        <div className="w-full px-6 md:px-12 max-w-3xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500/90 px-3 py-1 text-xs font-semibold text-primary-950">
            <i className="ri-fire-fill" />
            本季热映推荐
          </span>
          <h1 className="mt-4 font-heading text-3xl md:text-5xl font-extrabold text-background-50 leading-tight">
            {movies[index].title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
            <span className="flex items-center gap-1 font-semibold text-accent-400">
              <i className="ri-star-fill" />
              {movies[index].rating.toFixed(1)}
            </span>
            <span>{movies[index].year}</span>
            <span>{movies[index].region}</span>
            <span>{movies[index].genres.join(" · ")}</span>
          </div>
          <p className="mt-4 text-sm md:text-base text-background-200 leading-relaxed line-clamp-2 md:line-clamp-3 max-w-2xl">
            {movies[index].synopsis}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${movies[index].id}`}
              className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg" />
              查看详情
            </Link>
            <Link
              to={`/category?cat=${encodeURIComponent(movies[index].category)}`}
              className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50/15 border border-background-50/40 text-background-50 text-sm font-medium hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-list-check-2 text-lg" />
              更多{movies[index].category}
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute z-20 bottom-5 right-6 md:right-12 flex items-center gap-2">
        <button
          type="button"
          aria-label="上一张"
          onClick={() => goTo(index - 1)}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50/20 border border-background-50/40 text-background-50 hover:bg-background-50/35 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => goTo(index + 1)}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50/20 border border-background-50/40 text-background-50 hover:bg-background-50/35 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      </div>

      <div className="absolute z-20 bottom-7 left-6 md:left-12 flex items-center gap-2">
        {movies.map((movie, i) => (
          <button
            key={movie.id}
            type="button"
            aria-label={`切换到 ${movie.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}